import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { supabase } from '../lib/supabase';
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const NewsletterSignup: React.FC<{ variant?: 'card' | 'inline' }> = ({ variant = 'card' }) => {
  const { language } = useLanguage();
  const t = translations[language].blog;

  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'duplicate'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !agreed) return;

    setStatus('loading');
    try {
      const { error } = await supabase
        .from('blog_newsletter')
        .insert({ email, language_preference: language });

      if (error) {
        if (error.code === '23505') {
          setStatus('duplicate');
        } else {
          setStatus('error');
        }
      } else {
        setStatus('success');
        setEmail('');
        setAgreed(false);
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className={`${variant === 'card' ? 'rounded-2xl p-8' : ''} text-center`}
        style={variant === 'card' ? { background: 'linear-gradient(135deg, #115d7b 0%, #0d4a63 100%)' } : {}}>
        <CheckCircle className="w-12 h-12 text-white mx-auto mb-4" />
        <p className="text-white text-lg font-semibold mb-2">{t.newsletterSuccess}</p>
      </div>
    );
  }

  const cardClass = variant === 'card'
    ? 'rounded-2xl p-8 md:p-10'
    : 'py-6';

  return (
    <div
      className={cardClass}
      style={variant === 'card' ? { background: 'linear-gradient(135deg, #115d7b 0%, #0d4a63 100%)' } : {}}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-xl font-bold text-white">{t.newsletterTitle}</h3>
      </div>
      <p className="text-white/80 text-sm mb-5 max-w-lg">{t.newsletterSubtitle}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.newsletterPlaceholder}
            className="flex-1 px-4 py-3 rounded-xl bg-white/95 border-0 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
          <button
            type="submit"
            disabled={status === 'loading' || !agreed}
            className="px-6 py-3 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: '#ff8c00' }}
          >
            {status === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
            {t.newsletterButton}
          </button>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-white/30 bg-white/10"
          />
          <span className="text-white/70 text-xs">{t.newsletterPrivacy}</span>
        </label>

        {status === 'error' && (
          <div className="flex items-center gap-2 text-red-200 text-sm">
            <AlertCircle className="w-4 h-4" />
            {t.newsletterError}
          </div>
        )}
        {status === 'duplicate' && (
          <div className="flex items-center gap-2 text-yellow-200 text-sm">
            <AlertCircle className="w-4 h-4" />
            {t.newsletterDuplicate}
          </div>
        )}
      </form>
    </div>
  );
};

export default NewsletterSignup;
